// src/components/Codebook/CodebookDualCodeBlock.tsx
// Dual code block component for comparative learning (side-by-side language comparison)

import React, { useState, useEffect } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { analyticsApi } from '../../services/api';
import { useTheme } from '../../contexts/ThemeContext';

interface CodebookDualCodeBlockProps {
  language_primary: string;
  code_primary: string;
  language_secondary: string;
  code_secondary: string;
  comparison_notes?: string;
  codebookId?: string;
  blockIndex?: number;
  readOnly?: boolean;
}

interface ExecutionState {
  isExecuting: boolean;
  showOutput: boolean;
  result: {
    output?: string;
    error?: string;
    success?: boolean;
    validation_errors?: string[];
    generated_files?: Array<{ filename: string; image_base64?: string; image_mime?: string; size: number }>;
  } | null;
}

const CodebookDualCodeBlock: React.FC<CodebookDualCodeBlockProps> = ({
  language_primary,
  code_primary,
  language_secondary,
  code_secondary,
  comparison_notes,
  codebookId,
  blockIndex,
  readOnly = false
}) => {
  // oneDark is a dark-background theme; its light-gray base text washes out on light cards
  const { theme } = useTheme();
  const syntaxStyle = theme === 'dark' ? oneDark : oneLight;
  // Independent execution states for each side
  const [executionState1, setExecutionState1] = useState<ExecutionState>({
    isExecuting: false,
    showOutput: false,
    result: null
  });

  const [executionState2, setExecutionState2] = useState<ExecutionState>({
    isExecuting: false,
    showOutput: false,
    result: null
  });

  // Load last execution results when component mounts (skip in readOnly mode)
  useEffect(() => {
    if (!readOnly && codebookId && blockIndex !== undefined) {
      loadLastExecutions();
    }
  }, [codebookId, blockIndex, readOnly]);

  const loadLastExecutions = async () => {
    if (!codebookId || blockIndex === undefined) return;

    try {
      const token = localStorage.getItem('token');
      const authHeaders = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };

      // Try to load primary execution
      const response1 = await fetch(
        `/api/codebooks/${codebookId}/executions/latest?block_index=${blockIndex}&sub_block=primary`,
        { method: 'GET', headers: authHeaders }
      );

      if (response1.ok) {
        const data1 = await response1.json();
        if (data1.execution) {
          const details1 = data1.execution.execution_details || {};
          const savedFiles1 = details1.generated_files || [];
          setExecutionState1({
            isExecuting: false,
            showOutput: true,
            result: {
              output: data1.execution.stdout || '',
              error: data1.execution.stderr || data1.execution.error_message || '',
              success: data1.execution.success ?? true,
              validation_errors: [],
              generated_files: savedFiles1
            }
          });
        }
      }

      // Try to load secondary execution
      const response2 = await fetch(
        `/api/codebooks/${codebookId}/executions/latest?block_index=${blockIndex}&sub_block=secondary`,
        { method: 'GET', headers: authHeaders }
      );

      if (response2.ok) {
        const data2 = await response2.json();
        if (data2.execution) {
          const details2 = data2.execution.execution_details || {};
          const savedFiles2 = details2.generated_files || [];
          setExecutionState2({
            isExecuting: false,
            showOutput: true,
            result: {
              output: data2.execution.stdout || '',
              error: data2.execution.stderr || data2.execution.error_message || '',
              success: data2.execution.success ?? true,
              validation_errors: [],
              generated_files: savedFiles2
            }
          });
        }
      }
    } catch (err) {
      console.error('[CodebookDualCodeBlock] Error loading last executions:', err);
    }
  };

  const handleExecuteCode = async (side: 'primary' | 'secondary') => {
    const code = side === 'primary' ? code_primary : code_secondary;
    const language = side === 'primary' ? language_primary : language_secondary;
    const setState = side === 'primary' ? setExecutionState1 : setExecutionState2;

    setState(prev => ({ ...prev, isExecuting: true, showOutput: true, result: null }));

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const session_id = urlParams.get('session_id') || `codebook_${Date.now()}`;

      const result = await analyticsApi.executeCode({
        code,
        language,
        session_id
      });

      const executionResult = {
        output: result.output || result.stdout || '',
        error: result.error || result.stderr || '',
        success: result.success ?? true,
        validation_errors: result.validation_errors,
        generated_files: result.generated_files || []
      };

      setState(prev => ({ ...prev, result: executionResult }));

      // Save execution result to backend for persistence
      if (codebookId && blockIndex !== undefined) {
        try {
          const saveUrl = `/api/codebooks/${codebookId}/blocks/${blockIndex}/execute`;
          const savePayload = {
            language,
            code,
            sub_block: side,
            execution_result: {
              success: result.success ?? true,
              stdout: result.output || result.stdout || '',
              stderr: result.error || result.stderr || '',
              execution_time: result.execution_time || 0,
              executor: result.executor || 'unknown',
              error: result.error || '',
              generated_files: result.generated_files || []
            }
          };

          const saveToken = localStorage.getItem('token');
          await fetch(saveUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(saveToken ? { 'Authorization': `Bearer ${saveToken}` } : {})
            },
            body: JSON.stringify(savePayload)
          });
        } catch (saveErr) {
          console.error('[CodebookDualCodeBlock] Error saving execution:', saveErr);
        }
      }
    } catch (err: any) {
      console.error('[CodebookDualCodeBlock] Execution error:', err);
      setState(prev => ({
        ...prev,
        result: {
          error: err.response?.data?.detail || err.message || 'Code execution failed',
          success: false
        }
      }));
    } finally {
      setState(prev => ({ ...prev, isExecuting: false }));
    }
  };

  const handleCopyCode = async (code: string, language: string, side: string) => {
    try {
      await navigator.clipboard.writeText(code);
      const btnId = `copy-dual-${codebookId}-${blockIndex}-${side}`;
      const btn = document.querySelector(`[data-copy-btn="${btnId}"]`) as HTMLButtonElement;
      if (btn) {
        const originalText = btn.textContent;
        btn.textContent = 'Copied!';
        setTimeout(() => {
          btn.textContent = originalText;
        }, 2000);
      }
    } catch (err) {
      console.error('[CodebookDualCodeBlock] Copy error:', err);
    }
  };

  const CodePanel = ({
    language,
    code,
    executionState,
    onExecute,
    label,
    side
  }: {
    language: string;
    code: string;
    executionState: ExecutionState;
    onExecute: () => void;
    label: string;
    side: string;
  }) => (
    <div className="flex flex-col h-full">
      {/* Code header */}
      <div className={`flex items-center justify-between bg-card border-b ${theme === 'dark' ? 'border-white/10' : 'border-border'} px-4 py-2`}>
        <span className="text-xs font-mono text-muted-foreground">{language.toUpperCase()}</span>
        <div className="flex gap-2">
          <button
            onClick={() => handleCopyCode(code, language, side)}
            data-copy-btn={`copy-dual-${codebookId}-${blockIndex}-${side}`}
            className="px-2 py-1 text-xs bg-background hover:bg-card border border-border rounded transition-colors hover:text-primary"
          >
            Copy
          </button>
          {!readOnly && ['python', 'javascript', 'java', 'cpp', 'c', 'go', 'rust', 'ruby'].includes(language.toLowerCase()) && (
            <button
              onClick={onExecute}
              disabled={executionState.isExecuting}
              className="px-2 py-1 text-xs bg-primary hover:bg-primary/90 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              {executionState.isExecuting ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent"></div>
                  Running...
                </>
              ) : (
                <>
                  ▶ Run
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Code with syntax highlighting */}
      <div className="relative bg-card overflow-auto flex-1 min-h-[100px] max-h-[500px]">
        <SyntaxHighlighter
          language={language}
          style={syntaxStyle}
          codeTagProps={{ style: { background: 'transparent' } }}
          customStyle={{
            margin: 0,
            padding: '1rem',
            fontSize: '0.875rem',
            lineHeight: '1.5',
            backgroundColor: 'transparent'
          }}
        >
          {code}
        </SyntaxHighlighter>
      </div>

      {/* Execution output */}
      {executionState.showOutput && executionState.result && (
        <div
          className={`border-t ${theme === 'dark' ? 'border-white/10' : 'border-border'} p-4 ${
            executionState.result.success
              ? 'bg-green-500/10'
              : 'bg-red-500/10'
          }`}
        >
          {executionState.result.validation_errors &&
          executionState.result.validation_errors.length > 0 ? (
            <>
              <h4 className="font-semibold text-sm mb-2 text-red-600">Syntax Errors</h4>
              <div className="space-y-2">
                {executionState.result.validation_errors.map((error, idx) => (
                  <div
                    key={idx}
                    className="text-sm font-mono bg-red-500/5 border border-red-500/20 rounded px-3 py-2 text-foreground"
                  >
                    {error}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <h4 className="font-semibold text-sm mb-2">
                {executionState.result.success ? 'Output' : 'Error'}
              </h4>
              {/* Inline image preview */}
              {executionState.result.success && executionState.result.generated_files?.some(f => f.image_base64) && (
                <div className="mb-2 space-y-2">
                  {executionState.result.generated_files!.filter(f => f.image_base64).map((file, imgIdx) => (
                    <div key={imgIdx} className="border border-border rounded-lg overflow-hidden bg-[#1a1a2e] flex flex-col items-center">
                      <div className="p-4 flex items-center justify-center w-full">
                        <img
                          src={`data:${file.image_mime || 'image/png'};base64,${file.image_base64}`}
                          alt={file.filename}
                          className="max-w-full max-h-[500px] object-contain rounded"
                        />
                      </div>
                      <div className="px-3 py-1.5 bg-muted/50 text-xs text-muted-foreground flex justify-between items-center w-full">
                        <span>{file.filename}</span>
                        <span>{(file.size / 1024).toFixed(1)} KB</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <pre className="text-sm text-foreground whitespace-pre-wrap break-words font-mono">
                {executionState.result.success
                  ? executionState.result.output
                  : executionState.result.error}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Comparison notes — quiet narration above the code, accent line on the left,
          no box / no tint so it reads as prose rather than a callout. */}
      {comparison_notes && (
        <p className="text-sm font-normal italic leading-relaxed text-foreground/80 border-l-2 border-primary/40 pl-4 py-1">
          {comparison_notes}
        </p>
      )}

      {/* Two-column layout: Desktop side-by-side, Mobile stacked */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left panel - Primary language (known language) */}
        <div className={`relative flex flex-col border-2 ${theme === 'dark' ? 'border-white' : 'border-border'} rounded-lg overflow-hidden`}>
          {/* Purple gradient accent at top */}
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 z-10"></div>

          <CodePanel
            language={language_primary}
            code={code_primary}
            executionState={executionState1}
            onExecute={() => handleExecuteCode('primary')}
            label={language_primary}
            side="primary"
          />
        </div>

        {/* Right panel - Secondary language (learning language) */}
        <div className={`relative flex flex-col border-2 ${theme === 'dark' ? 'border-white' : 'border-border'} rounded-lg overflow-hidden`}>
          {/* Purple gradient accent at top */}
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 z-10"></div>

          <CodePanel
            language={language_secondary}
            code={code_secondary}
            executionState={executionState2}
            onExecute={() => handleExecuteCode('secondary')}
            label={language_secondary}
            side="secondary"
          />
        </div>
      </div>
    </div>
  );
};

export default CodebookDualCodeBlock;
